/**
 * JSON ⇄ typed in-memory shape for the wire formats every receiver
 * (cast endpoint, verifier, audit app) consumes. Centralized so all
 * receivers parse identically — divergent parsers would silently
 * accept transcripts the others reject.
 *
 * Encoding contract: every point is 32-byte canonical Ristretto255
 * hex; every scalar is 32-byte little-endian hex (decoder rejects
 * out-of-range). All parsers throw `SerdeError` on shape miss; never
 * return undefined.
 *
 * STATUS: M4 (cast endpoint). The CLIs still carry their own inline
 * parsers; folding them onto this module is a separate cleanup.
 */
import type {
  BenalohChallenge,
  ChaumPedersenProof,
  DisjunctiveProof,
  DkgTranscript,
  ElGamalCiphertext,
  EncryptedBallot,
  PartialDecryption,
  SchnorrSignature,
  TallyTranscript,
  Transcript,
  Trustee,
} from "./types";
import {
  bytesToHex,
  canonicalJsonStringify,
  decodePoint,
  decodeScalar,
  encodePoint,
  encodeScalar,
  hexToBytes,
  sha256,
} from "./internal/encoding";

export {
  bytesToHex,
  canonicalJsonStringify,
  decodePoint,
  decodeScalar,
  encodePoint,
  encodeScalar,
  hexToBytes,
};

export class SerdeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SerdeError";
  }
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function asObject(v: unknown, ctx: string): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new SerdeError(`${ctx}: expected object`);
  }
  return v as Record<string, unknown>;
}

function asString(v: unknown, ctx: string): string {
  if (typeof v !== "string") throw new SerdeError(`${ctx}: expected string`);
  return v;
}

function asArray(v: unknown, ctx: string): unknown[] {
  if (!Array.isArray(v)) throw new SerdeError(`${ctx}: expected array`);
  return v;
}

function asInt(v: unknown, ctx: string): number {
  if (typeof v !== "number" || !Number.isInteger(v)) {
    throw new SerdeError(`${ctx}: expected integer`);
  }
  return v;
}

function asArrayOf<T>(
  v: unknown,
  ctx: string,
  parseEach: (item: unknown, ictx: string) => T,
): T[] {
  return asArray(v, ctx).map((item, i) => parseEach(item, `${ctx}[${i}]`));
}

// ---------------------------------------------------------------------------
// Field parsers
// ---------------------------------------------------------------------------

function parseCiphertext(v: unknown, ctx: string): ElGamalCiphertext {
  const o = asObject(v, ctx);
  return {
    c1: decodePoint(hexToBytes(asString(o["c1"], `${ctx}.c1`))),
    c2: decodePoint(hexToBytes(asString(o["c2"], `${ctx}.c2`))),
  };
}

function parseDisjunctive(v: unknown, ctx: string): DisjunctiveProof {
  const o = asObject(v, ctx);
  return {
    t1_0: decodePoint(hexToBytes(asString(o["t1_0"], `${ctx}.t1_0`))),
    t2_0: decodePoint(hexToBytes(asString(o["t2_0"], `${ctx}.t2_0`))),
    t1_1: decodePoint(hexToBytes(asString(o["t1_1"], `${ctx}.t1_1`))),
    t2_1: decodePoint(hexToBytes(asString(o["t2_1"], `${ctx}.t2_1`))),
    e0: decodeScalar(hexToBytes(asString(o["e0"], `${ctx}.e0`))),
    s0: decodeScalar(hexToBytes(asString(o["s0"], `${ctx}.s0`))),
    e1: decodeScalar(hexToBytes(asString(o["e1"], `${ctx}.e1`))),
    s1: decodeScalar(hexToBytes(asString(o["s1"], `${ctx}.s1`))),
  };
}

function parseChaumPedersen(v: unknown, ctx: string): ChaumPedersenProof {
  const o = asObject(v, ctx);
  return {
    t1: decodePoint(hexToBytes(asString(o["t1"], `${ctx}.t1`))),
    t2: decodePoint(hexToBytes(asString(o["t2"], `${ctx}.t2`))),
    s: decodeScalar(hexToBytes(asString(o["s"], `${ctx}.s`))),
  };
}

function parseSignature(v: unknown, ctx: string): SchnorrSignature {
  const o = asObject(v, ctx);
  return {
    R: decodePoint(hexToBytes(asString(o["R"], `${ctx}.R`))),
    s: decodeScalar(hexToBytes(asString(o["s"], `${ctx}.s`))),
  };
}

// ---------------------------------------------------------------------------
// Top-level parsers
// ---------------------------------------------------------------------------

export function parseEncryptedBallot(
  v: unknown,
  ctx = "ballot",
): EncryptedBallot {
  const o = asObject(v, ctx);
  return {
    ballotId: asString(o["ballotId"], `${ctx}.ballotId`),
    credentialPublicKey: decodePoint(
      hexToBytes(asString(o["credentialPublicKey"], `${ctx}.credentialPublicKey`)),
    ),
    ciphertexts: asArray(o["ciphertexts"], `${ctx}.ciphertexts`).map((c, i) =>
      parseCiphertext(c, `${ctx}.ciphertexts[${i}]`),
    ),
    perOptionProofs: asArray(o["perOptionProofs"], `${ctx}.perOptionProofs`).map(
      (p, i) => parseDisjunctive(p, `${ctx}.perOptionProofs[${i}]`),
    ),
    sumProof: parseChaumPedersen(o["sumProof"], `${ctx}.sumProof`),
    signature: parseSignature(o["signature"], `${ctx}.signature`),
  };
}

export function parseDkgTranscript(v: unknown, ctx = "dkg"): DkgTranscript {
  const o = asObject(v, ctx);
  return {
    threshold: asInt(o["threshold"], `${ctx}.threshold`),
    trustees: asArray(o["trustees"], `${ctx}.trustees`).map((t, i): Trustee => {
      const ot = asObject(t, `${ctx}.trustees[${i}]`);
      return {
        id: asString(ot["id"], `${ctx}.trustees[${i}].id`),
        index: asInt(ot["index"], `${ctx}.trustees[${i}].index`),
        name: asString(ot["name"], `${ctx}.trustees[${i}].name`),
        verificationKey: decodePoint(
          hexToBytes(
            asString(ot["verificationKey"], `${ctx}.trustees[${i}].verificationKey`),
          ),
        ),
      };
    }),
    feldmanCommitments: asArray(
      o["feldmanCommitments"],
      `${ctx}.feldmanCommitments`,
    ).map((row, i) =>
      asArray(row, `${ctx}.feldmanCommitments[${i}]`).map((h, k) =>
        decodePoint(hexToBytes(asString(h, `${ctx}.feldmanCommitments[${i}][${k}]`))),
      ),
    ),
    combinedPublicKey: decodePoint(
      hexToBytes(asString(o["combinedPublicKey"], `${ctx}.combinedPublicKey`)),
    ),
  };
}

export function parsePartialDecryption(
  v: unknown,
  ctx = "partial",
): PartialDecryption {
  const o = asObject(v, ctx);
  return {
    trusteeId: asString(o["trusteeId"], `${ctx}.trusteeId`),
    perOption: asArray(o["perOption"], `${ctx}.perOption`).map((p, i) => {
      const op = asObject(p, `${ctx}.perOption[${i}]`);
      return {
        decryptionShare: decodePoint(
          hexToBytes(
            asString(op["decryptionShare"], `${ctx}.perOption[${i}].decryptionShare`),
          ),
        ),
        zkProof: parseChaumPedersen(
          op["zkProof"],
          `${ctx}.perOption[${i}].zkProof`,
        ),
      };
    }),
  };
}

function parseTallyTranscript(v: unknown, ctx: string): TallyTranscript {
  const o = asObject(v, ctx);
  return {
    counts: asArrayOf(o["counts"], `${ctx}.counts`, (c, ictx) => asInt(c, ictx)),
    trusteeSubset: asArrayOf(
      o["trusteeSubset"],
      `${ctx}.trusteeSubset`,
      (s, ictx) => asString(s, ictx),
    ),
    partialDecryptions: asArrayOf(
      o["partialDecryptions"],
      `${ctx}.partialDecryptions`,
      (p, ictx) => parsePartialDecryption(p, ictx),
    ),
    computedAt: asString(o["computedAt"], `${ctx}.computedAt`),
  };
}

/**
 * Top-level parser for the full bulletin transcript that the verifier
 * CLI and the auditor app consume.
 */
export function parseTranscript(v: unknown, ctx = "transcript"): Transcript {
  const o = asObject(v, ctx);
  return {
    ballotId: asString(o["ballotId"], `${ctx}.ballotId`),
    title: asString(o["title"], `${ctx}.title`),
    options: asArrayOf(o["options"], `${ctx}.options`, (opt, ictx) => {
      const oo = asObject(opt, ictx);
      return {
        id: asString(oo["id"], `${ictx}.id`),
        label: asString(oo["label"], `${ictx}.label`),
      };
    }),
    dkg: parseDkgTranscript(o["dkg"], `${ctx}.dkg`),
    bulletin: asArrayOf(o["bulletin"], `${ctx}.bulletin`, (e, ictx) => {
      const oe = asObject(e, ictx);
      return {
        encrypted: parseEncryptedBallot(oe["encrypted"], `${ictx}.encrypted`),
        receivedAt: asString(oe["receivedAt"], `${ictx}.receivedAt`),
      };
    }),
    tally: parseTallyTranscript(o["tally"], `${ctx}.tally`),
    // seats defaults to 1 for legacy single-choice transcripts that
    // were published before the multi-winner extension.
    seats: typeof o["seats"] === "number" && o["seats"] >= 1 ? Math.floor(o["seats"]) : 1,
    signedAt: asString(o["signedAt"], `${ctx}.signedAt`),
    signedBy: asString(o["signedBy"], `${ctx}.signedBy`),
    // signerPublicKey is optional for back-compat with already-published
    // pre-Ed25519 transcripts; the verifier treats "" as "absent".
    signerPublicKey:
      typeof o["signerPublicKey"] === "string" ? o["signerPublicKey"] : "",
    signature: asString(o["signature"], `${ctx}.signature`),
  };
}

// ---------------------------------------------------------------------------
// Fingerprints
// ---------------------------------------------------------------------------

/**
 * Hex SHA-256 of the credential public key bytes — what
 * `voting.eligibility.voter_credential_hash` stores. The cast endpoint
 * computes this on the incoming ballot and looks it up against the
 * eligibility table to decide whether the credential is allowed.
 */
export function credentialFingerprint(ballot: EncryptedBallot): string {
  return bytesToHex(sha256(encodePoint(ballot.credentialPublicKey)));
}

/**
 * Hex of the credential public key itself — what gets published on the
 * bulletin (no PII).
 */
export function credentialPublicKeyHex(ballot: EncryptedBallot): string {
  return bytesToHex(encodePoint(ballot.credentialPublicKey));
}

// ---------------------------------------------------------------------------
// Serializers (typed → canonical JSON-able value)
// ---------------------------------------------------------------------------

/**
 * Inverse of `parseEncryptedBallot`. Returns a JSON-able value with hex
 * fields; callers wrap with `canonicalJsonStringify` to obtain stable
 * UTF-8 bytes for storage or signing.
 */
export function toEncryptedBallotJson(b: EncryptedBallot): unknown {
  return {
    ballotId: b.ballotId,
    credentialPublicKey: bytesToHex(encodePoint(b.credentialPublicKey)),
    ciphertexts: b.ciphertexts.map((c) => ({
      c1: bytesToHex(encodePoint(c.c1)),
      c2: bytesToHex(encodePoint(c.c2)),
    })),
    perOptionProofs: b.perOptionProofs.map((p) => ({
      t1_0: bytesToHex(encodePoint(p.t1_0)),
      t2_0: bytesToHex(encodePoint(p.t2_0)),
      t1_1: bytesToHex(encodePoint(p.t1_1)),
      t2_1: bytesToHex(encodePoint(p.t2_1)),
      e0: bytesToHex(encodeScalar(p.e0)),
      s0: bytesToHex(encodeScalar(p.s0)),
      e1: bytesToHex(encodeScalar(p.e1)),
      s1: bytesToHex(encodeScalar(p.s1)),
    })),
    sumProof: {
      t1: bytesToHex(encodePoint(b.sumProof.t1)),
      t2: bytesToHex(encodePoint(b.sumProof.t2)),
      s: bytesToHex(encodeScalar(b.sumProof.s)),
    },
    signature: {
      R: bytesToHex(encodePoint(b.signature.R)),
      s: bytesToHex(encodeScalar(b.signature.s)),
    },
  };
}

/**
 * Result of parsing a Benaloh audit-challenge JSON. The challenge
 * itself is carried separately from the advertised ballot public key:
 * the verifier compares the advertised key against a trusted source
 * (the official bulletin board) before invoking
 * `verifyBenalohChallenge` — the on-disk file is voter-supplied and
 * cannot be assumed authentic on its own.
 */
export interface ParsedBenalohChallenge {
  readonly challenge: BenalohChallenge;
  readonly advertisedBallotPublicKey: ReturnType<typeof decodePoint>;
}

/**
 * Inverse of `parseBenalohChallenge`. The audit JSON shape carries hex
 * fields and a top-level `kind` discriminator so dropper UIs can reject
 * cast ballots that were posted by mistake. `ballotPublicKey` is
 * included for self-contained verification — the voter is expected to
 * cross-check it against the public bulletin before trusting the
 * verifier output.
 */
export function toBenalohChallengeJson(
  c: BenalohChallenge,
  ballotPublicKey: ReturnType<typeof decodePoint>,
): unknown {
  return {
    kind: "BenalohChallenge",
    version: 2,
    ballotId: c.ballotId,
    ballotPublicKey: bytesToHex(encodePoint(ballotPublicKey)),
    credentialPublicKey: bytesToHex(encodePoint(c.credentialPublicKey)),
    options: [...c.options],
    seats: c.seats,
    chosen: [...c.chosen],
    randomnesses: c.randomnesses.map((r) => bytesToHex(encodeScalar(r))),
    ciphertexts: c.ciphertexts.map((ct) => ({
      c1: bytesToHex(encodePoint(ct.c1)),
      c2: bytesToHex(encodePoint(ct.c2)),
    })),
  };
}

/**
 * Parse a Benaloh challenge audit JSON. Throws SerdeError on missing
 * fields, wrong `kind`, malformed hex, or option/randomness/ciphertext
 * length divergence. Returns the challenge and the advertised ballot
 * public key separately — see `ParsedBenalohChallenge` for the trust
 * boundary.
 */
export function parseBenalohChallenge(
  v: unknown,
  ctx = "challenge",
): ParsedBenalohChallenge {
  const o = asObject(v, ctx);
  const kind = asString(o["kind"], `${ctx}.kind`);
  if (kind !== "BenalohChallenge") {
    throw new SerdeError(`${ctx}.kind: expected "BenalohChallenge", got "${kind}"`);
  }
  const options = asArrayOf(o["options"], `${ctx}.options`, asString);
  // chosen is an array (v2). Tolerate v1 (single string) by wrapping.
  let chosen: string[];
  const chosenRaw = o["chosen"];
  if (Array.isArray(chosenRaw)) {
    chosen = chosenRaw.map((v, i) => asString(v, `${ctx}.chosen[${i}]`));
  } else if (typeof chosenRaw === "string") {
    chosen = [chosenRaw];
  } else {
    throw new SerdeError(`${ctx}.chosen: expected string[] or string`);
  }
  for (const c of chosen) {
    if (options.indexOf(c) < 0) {
      throw new SerdeError(`${ctx}.chosen: ${c} not in options`);
    }
  }
  // seats defaults to chosen.length when missing (covers v1 challenges
  // where seats was implicitly 1).
  const seatsRaw = o["seats"];
  const seats =
    typeof seatsRaw === "number" && seatsRaw >= 1 ? Math.floor(seatsRaw) : chosen.length;
  if (chosen.length !== seats) {
    throw new SerdeError(
      `${ctx}: chosen length ${chosen.length} != seats ${seats}`,
    );
  }
  const randomnesses = asArrayOf(o["randomnesses"], `${ctx}.randomnesses`, (h, c) =>
    decodeScalar(hexToBytes(asString(h, c))),
  );
  if (randomnesses.length !== options.length) {
    throw new SerdeError(
      `${ctx}: randomnesses length ${randomnesses.length} != options length ${options.length}`,
    );
  }
  const ciphertexts = asArrayOf(o["ciphertexts"], `${ctx}.ciphertexts`, parseCiphertext);
  if (ciphertexts.length !== options.length) {
    throw new SerdeError(
      `${ctx}: ciphertexts length ${ciphertexts.length} != options length ${options.length}`,
    );
  }
  return {
    challenge: {
      ballotId: asString(o["ballotId"], `${ctx}.ballotId`),
      credentialPublicKey: decodePoint(
        hexToBytes(asString(o["credentialPublicKey"], `${ctx}.credentialPublicKey`)),
      ),
      options,
      seats,
      chosen,
      randomnesses,
      ciphertexts,
    },
    advertisedBallotPublicKey: decodePoint(
      hexToBytes(asString(o["ballotPublicKey"], `${ctx}.ballotPublicKey`)),
    ),
  };
}

/**
 * Schnorr signature concatenation `R || s` (32 + 32 = 64 bytes). Used
 * for compact storage in the `voting.cast.client_signature` column.
 */
export function signatureRawBytes(s: SchnorrSignature): Uint8Array {
  const R = encodePoint(s.R);
  const sBytes = encodeScalar(s.s);
  const out = new Uint8Array(R.length + sBytes.length);
  out.set(R, 0);
  out.set(sBytes, R.length);
  return out;
}
